import React, { useState, useContext } from 'react';
import {
  makeStyles,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@material-ui/core';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { API, graphqlOperation, Storage } from 'aws-amplify';
import { createGuidance, createGuidanceContent } from '../graphql/mutations';
import { UserContext } from '../App';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  textArea: {
    width: '80%',
    margin: theme.spacing(2),
  },
  submitButton: {
    backgroundColor: '#01b5e2',
    color: 'white',
    '&:hover': {
      backgroundColor: '#0193b7',
    },
    padding: theme.spacing(1),
    margin: theme.spacing(1),
  },
  resultContainer: {
    width: '80%',
    marginTop: theme.spacing(4),
  },
  pageContainer: {
    marginTop: theme.spacing(2),
  },
  image: {
    width: '100%',
    maxWidth: '256px',
    height: 'auto',
    marginTop: theme.spacing(2),
  },
  radioGroup: {
    marginTop: theme.spacing(2),
    flexDirection: 'row',  // Override to display in row direction
  },

}));

const GenerateArticles = () => {
  const classes = useStyles();
  const [articleTitle, setArticleTitle] = useState('');
  const [articleData, setArticleData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]); // Store images
  const [selectedImage, setSelectedImage] = useState(''); // Store the selected image URL
  const [progressMessage, setProgressMessage] = useState(''); // Store progress messages
  const userId = useContext(UserContext);

  const handleSubmit = async () => {
    if (!articleTitle.trim()) {
      return;
    }
    setLoading(true);
    setProgressMessage('Generating article description...');

    try {
      // Step 1: Generate the article components
      const articleResponse = await axios.post(
        'https://gptapi.acecura.com/api/gpt-create-article',
        { articleTitle },
        {
          headers: {
            'x-api-key': '56a613f1-9d5a-4cc8-8a69-ad06e603397d',
            'Content-Type': 'application/json',
          },
          timeout: 120000, // 120 seconds timeout
        }
      );

      const article = articleResponse.data;

      if (!article.pages || !article.pages.length) {
        throw new Error('No pages found in the article response');
      }

      setProgressMessage('Generating page content...');

      // Step 2: Generate content for each page
      const pageContents = await Promise.all(
        article.pages.map(async (page) => {
          const contentResponse = await axios.post(
            'https://gptapi.acecura.com/api/gpt-create-page-content',
            {
              pageTitle: page.pageTitle,
              articleDescription: article.shortDescription,
              pageContext: page.pageContext,
            },
            {
              headers: {
                'x-api-key': '56a613f1-9d5a-4cc8-8a69-ad06e603397d',
              },
              timeout: 120000, // 120 seconds timeout
            }
          );

          return {
            ...page,
            ...contentResponse.data,
          };
        })
      );

      setProgressMessage('Generating images...');

      // Step 3: Collect image prompts
      const imagePrompts = [
        article.imagePrompt,
        ...pageContents.map((page) => page.recommendedImagePrompt),
      ];

      // Step 4: Generate images for each prompt
      const fetchedImages = await Promise.all(
        imagePrompts.map(async (prompt) => {
          try {
            const imageResponse = await axios.post(
              'https://gptapi.acecura.com/api/gpt-article-image-generation',
              { imagePrompt: prompt },
              {
                headers: {
                  'x-api-key': '56a613f1-9d5a-4cc8-8a69-ad06e603397d',
                  'Content-Type': 'application/json',
                },
                timeout: 240000, // 240 seconds timeout
              }
            );
            return imageResponse.data; // Return the image data directly
          } catch (error) {
            return null; // Return null for failed requests
          }
        })
      );

      const validImages = fetchedImages.filter((img) => img !== null);
      setImages(validImages);

      // Combine all data
      setArticleData({
        ...article,
        pages: pageContents,
      });

      setProgressMessage('Article generation completed.');
    } catch (error) {
      console.error('Error generating article:', error);
      setProgressMessage('Error during article generation.');
    } finally {
      setLoading(false);
    }
  };

  const fetchImageFromServer = async (imageUrl) => {
    try {
      const response = await fetch(`https://gptapi.acecura.com/api/fetch-gpt-image?url=${encodeURIComponent(imageUrl)}`, {
        method: 'GET',
        headers: {
          'x-api-key': '56a613f1-9d5a-4cc8-8a69-ad06e603397d', // Add your API key here
        },
      });
  
      if (!response.ok) {
        throw new Error('Error fetching image from server');
      }
  
      // Convert the image response to a blob
      return await response.blob();
    } catch (error) {
      console.error('Error fetching image from Express server:', error);
      throw error;
    }
  };
  

  const handleImageUploadAndSubmit = async () => {
    if (!selectedImage) return;
  
    try {
  
      // Fetch the image from the Express server
      const imageBlob = await fetchImageFromServer(selectedImage);
  
      // Check if imageBlob is valid
      if (!imageBlob) {
        throw new Error('Failed to fetch imageBlob');
      }
  
      // Convert the Blob to an ArrayBuffer
      const arrayBuffer = await imageBlob.arrayBuffer();
  
      // Extract the file extension by removing any query parameters (anything after '?')
      const cleanedImageUrl = selectedImage.split('?')[0]; // Get rid of query parameters
      const fileExtension = cleanedImageUrl.split('.').pop(); // Extract the file extension
      const fileName = `${articleTitle.replace(/\s+/g, '-').toLowerCase()}-${uuidv4()}.${fileExtension}`;

      // Upload the image to S3
      const key = `thumbnails/${fileName}`;
  
      const response = await Storage.put(key, arrayBuffer, {
        contentType: `image/${fileExtension}`,
      });
  
      const bucketKey = response.key;
  
      // Prepare the guidance payload
      const input = {
        title: articleTitle,
        lowerCaseTitle: articleTitle.toLowerCase(),
        longDesc: articleData.shortDescription,
        shortDesc: articleData.shortDescription,
        lowerCaseShortDesc: articleData.shortDescription.toLowerCase(),
        deleted: false,
        guidanceGuidanceTypesId: 'bcb976f1-46ef-4a6a-90d1-3e77ec70578b', // replace with actual type
        status: 'DRAFT',
        S3ObjectKey: bucketKey,
      };
  
      // Submit the article
      const submitArticle = await API.graphql(graphqlOperation(createGuidance, { input }));
      const articleId = submitArticle.data.createGuidance.id; // Get the newly created article ID
  
      // Submit content for each page
      const pageSubmissions = articleData.pages.map(async (page, index) => {
        const pageTitle = page.pageTitle;
        const htmlContent = page.pageContent;
  
        // Create the guidance content for each page
        const contentInput = {
          guidanceID: articleId, // Use the newly created article ID
          title: pageTitle,
          content: htmlContent,
          order: index + 1, // Assign an order to each page
        };
  
        return await API.graphql(graphqlOperation(createGuidanceContent, { input: contentInput }));
      });
  
      // Wait for all pages to be submitted
      await Promise.all(pageSubmissions);

    } catch (error) {
      console.error('Error uploading image and submitting article/pages:', error);
    }
  };
  
  return (
    <div className={classes.container}>
      <TextField
        className={classes.textArea}
        label="Article Title"
        variant="outlined"
        value={articleTitle}
        onChange={(e) => setArticleTitle(e.target.value)}
      />
      <Button
        className={classes.submitButton}
        onClick={handleSubmit}
        disabled={!articleTitle.trim() || loading}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Generate Article'}
      </Button>

      {/* Display progress message */}
      {loading && (
        <Typography variant="body1" color="textSecondary">
          {progressMessage}
        </Typography>
      )}

      {articleData && (
        <div className={classes.resultContainer}>
          <Typography variant="h4" gutterBottom>
            {articleTitle}
          </Typography>
          <Typography variant="body1">{articleData.shortDescription}</Typography>
          <br />
          <Typography variant="h5">Suggested images</Typography>

          {/* Render selectable images */}
          <RadioGroup
            className={classes.radioGroup}
            value={selectedImage}
            onChange={(e) => setSelectedImage(e.target.value)}
          >
            {images.map((image, index) => (
              <FormControlLabel
                key={index}
                value={image.url} // Use the image URL as the value
                control={<Radio />}
                label={
                  <img
                    src={image.url}
                    alt={`Generated image ${index}`}
                    className={classes.image}
                  />
                }
              />
            ))}
          </RadioGroup>

          {/* Upload selected image and submit article */}
          <Button
            className={classes.submitButton}
            onClick={handleImageUploadAndSubmit}
            disabled={!selectedImage}
          >
            CREATE NEW GUIDANCE ARTICLE
          </Button>

          {/* Display article pages */}
          {articleData.pages.map((page, index) => (
            <div key={index} className={classes.pageContainer}>
              <Typography variant="h5">{`Page ${index + 1}: ${page.pageTitle}`}</Typography>
              <Typography
                variant="body1"
                dangerouslySetInnerHTML={{ __html: page.pageContent }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GenerateArticles;
